import { useRef, useState } from "react";
import { attachmentDisplayClassifier } from "@/lib/AttachmentDisplayClassifier";
import { ChatAttachmentComposerController } from "./ChatAttachmentComposerController";

/**
 * Attach button + pending chips for chat/ticket reply composers.
 *
 * @param {{
 *   disabled?: boolean,
 *   controller: ChatAttachmentComposerController,
 *   onFilesSelected: (files: FileList) => void,
 *   layout?: "stacked" | "inlineButton" | "pendingOnly" — stacked: full row; inlineButton: tabs row; pendingOnly: chips only
 * }} props
 */
export default function ChatAttachmentComposer({
  disabled,
  controller,
  onFilesSelected,
  layout = "stacked",
}) {
  const fileRef = useRef(null);
  const [, setTick] = useState(0);
  const bump = () => setTick((n) => n + 1);

  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      multiple
      accept={attachmentDisplayClassifier.acceptAttribute()}
      className="sr-only"
      disabled={disabled}
      onChange={(e) => {
        if (e.target.files?.length) onFilesSelected(e.target.files);
        if (fileRef.current) fileRef.current.value = "";
        bump();
      }}
    />
  );

  const attachButton =
    layout === "inlineButton" ? (
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/90 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition-all hover:border-teal-300 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-teal-600"
        disabled={disabled}
        aria-label="Attach file"
        title="Attach a file, or paste an image with Ctrl+V"
        onClick={() => fileRef.current?.click()}
      >
        <i className="ri-attachment-2 text-sm" aria-hidden />
        Attach
      </button>
    ) : (
      <button
        type="button"
        className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 text-[11px] font-semibold text-slate-700 shadow-sm hover:border-teal-300 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
        disabled={disabled}
        aria-label="Attach file"
        onClick={() => fileRef.current?.click()}
      >
        <i className="ri-attachment-2 text-base" aria-hidden />
        <span className="hidden sm:inline">Attach</span>
      </button>
    );

  const pendingChips =
    controller.pending.length > 0 ? (
      <div className="flex flex-wrap items-center gap-2">
        {controller.pending.map((p) => (
          <span
            key={p.id}
            className="inline-flex max-w-[180px] items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200/80 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700"
          >
            <span className="truncate">{p.file_name}</span>
            {p.uploading ? <span className="text-slate-400">…</span> : null}
            <button
              type="button"
              className="text-slate-400 hover:text-rose-600"
              aria-label={`Remove ${p.file_name}`}
              disabled={disabled || p.uploading}
              onClick={() => {
                controller.removeById(p.id);
                bump();
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    ) : null;

  if (layout === "inlineButton") {
    return (
      <>
        {fileInput}
        {attachButton}
      </>
    );
  }

  if (layout === "pendingOnly") {
    if (!pendingChips) return null;
    return <div className="mb-2">{pendingChips}</div>;
  }

  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      {fileInput}
      {attachButton}
      {pendingChips}
    </div>
  );
}
